<template>
  <v-container>
    <v-row
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Marketing Campaigns
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.title"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
    </v-row>

    <v-row>
      <!--<editor-fold desc="Filters">-->
      <filters-column
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <v-subheader class="font-weight-medium">
          Client
        </v-subheader>
        <v-autocomplete
          v-model="filters.client_id"
          solo
          flat
          outlined
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="Select client..."
          dense
        />

        <v-subheader class="font-weight-medium">
          Currently
        </v-subheader>

        <v-list
          rounded
          color="transparent"
          class="pa-0"
        >
          <v-list-item-group
            v-model="filters.is_active"
            class="ma-0"
          >
            <v-list-item
              class="mb-1"
              active-class="primary--text"
              :value="true"
            >
              <template v-slot:default="{ active, toggle }">
                <v-list-item-action>
                  <v-simple-checkbox
                    color="primary"
                    :value="active"
                    :ripple="false"
                    @click="toggle"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    Active
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
            <v-list-item
              class="mb-1"
              active-class="primary--text"
              :value="false"
            >
              <template v-slot:default="{ active, toggle }">
                <v-list-item-action>
                  <v-simple-checkbox
                    color="primary"
                    :value="active"
                    :ripple="false"
                    @click="toggle"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    Inactive
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>

        <v-list
          rounded
          color="transparent"
          class="pa-0 mt-6"
        >
          <v-list-item
            class="mb-1"
            active-class="primary--text"
            :input-value="filters.include_expired"
            @click="filters.include_expired = !filters.include_expired"
          >
            <v-list-item-action>
              <v-simple-checkbox
                v-model="filters.include_expired"
                color="primary"
                :ripple="false"
              />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                Show expired campaigns
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
        />
        <v-data-table
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: playlist }">
            <tr
              class="clickable"
              @click="viewMarketingCampaign.playlistId = playlist.id; viewMarketingCampaign.dialog = true"
            >
              <td>
                <div class="subtitle-2 primary--text">
                  {{ playlist.title }}
                </div>
                <div class="caption text--secondary">
                  {{ playlist.description }}
                </div>
              </td>
              <td>
                <span v-if="playlist.client">{{ playlist.client.name }}</span>
                <span
                  v-else
                  class="grey--text"
                >
                  n/a
                </span>
              </td>
              <td>
                {{ playlist.library_files_count }}
              </td>
              <td>
                <span v-if="playlist.active_from">
                  {{ $dayjs(playlist.active_from).format('DD.MM.YYYY.') }}
                </span>
                <span
                  v-else
                  class="grey--text"
                >
                  unlimited
                </span>
              </td>
              <td>
                <span v-if="playlist.active_until">
                  {{ $dayjs(playlist.active_until).format('DD.MM.YYYY.') }}
                </span>
                <span
                  v-else
                  class="grey--text"
                >
                  unlimited
                </span>
              </td>
              <td>
                <v-chip
                  small
                  class="ma-0"
                  :color="playlist.is_active ? 'secondary' : 'grey lighten-2'"
                >
                  {{ playlist.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
              </td>
              <td />
            </tr>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
          />
        </div>
      </v-col>
    </v-row>

    <view-marketing-campaign-dialog
      v-if="viewMarketingCampaign.playlistId"
      :visible.sync="viewMarketingCampaign.dialog"
      :playlist-id="viewMarketingCampaign.playlistId"
      @playlist:updated="fetchItems()"
      @playlist:deleted="fetchItems()"
    />
  </v-container>
</template>

<script>
// @ is an alias to /src

import FiltersColumn from '@/components/helpers/FiltersColumn'
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import { cloneDeep } from 'lodash'
import PlaylistModel from '@/models/PlaylistModel'
import Clients from '@/services/Clients'
import Playlists from '@/services/Playlists'
import ViewMarketingCampaignDialog from '@/components/marketing/ViewMarketingCampaignDialog'

export default {
  name: 'MarketingCampaigns',
  components: {
    ViewMarketingCampaignDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin],
  data() {
    return {
      clients: {
        loading: false,
        data: []
      },
      viewMarketingCampaign: {
        dialog: false,
        playlistId: undefined
      },
      editMarketingCampaign: {
        dialog: false
      }
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(PlaylistModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Title', value: 'title' },
      { text: 'Client', value: 'client_name' },
      { text: 'Nr. of Ads', value: 'advertisement_count' },
      { text: 'Active From', value: 'active_from' },
      { text: 'Active To', value: 'active_until' },
      { text: 'Currently Active', value: 'is_active' },
      { sortable: false }
    ]

    this.fetchClients()
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await Playlists.fetch({
        content_type: PlaylistModel.CONTENT_TYPE_MARKETING,
        ...queryParams
      })
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }

}
</script>
